import { MetricGroupAssignment } from '@apps/materiality-tracker/components/metric-groups/MetricGroupAssignment';
import { AssignmentViewMode, ViewMode } from '@features/custom-metrics/constants';
import {
  MetricGroupConfiguration as MetricGroupDetails,
  MetricGroupField,
} from '@features/custom-metrics/MetricGroupConfiguration';
import { DashboardSection } from '@g17eco/molecules/dashboard';
import { Menu } from '@g17eco/molecules/menu/Menu';
import { ROUTES } from '@constants/routes';
import { PACK, QUESTION } from '@constants/terminology';
import { Breadcrumbs } from '@g17eco/molecules/breadcrumbs';
import { FieldProps } from '@g17eco/molecules/form';
import { MetricGroup } from '@g17eco/types/metricGroup';
import { generateUrl } from '@routes/util';
import { useHistory } from 'react-router-dom';
import { MetricGroupConfiguration } from './MetricGroupConfiguration';
import { OutdatedMetricGroupAlert } from './OutdatedMetricGroupAlert';

interface Props {
  initiativeId: string;
  metricGroup: MetricGroup;
  viewMode?: ViewMode;
  subGroup?: MetricGroup;
  assignmentViewMode?: AssignmentViewMode;
  handleReload: () => Promise<void>;
}

const metricGroupFields: FieldProps<MetricGroupField>[] = [
  {
    code: 'groupName',
    type: 'text',
    required: true,
    label: `${PACK.CAPITALIZED_SINGULAR} name`,
    disabled: true,
  },
  {
    code: 'description',
    type: 'textarea',
    required: false,
    label: `${PACK.CAPITALIZED_SINGULAR} description`,
    placeholder: 'Add description here...',
  },
];

export const MetricGroupForm = ({
  initiativeId,
  viewMode = ViewMode.Edit,
  metricGroup,
  subGroup,
  assignmentViewMode,
  handleReload,
}: Props) => {
  const rootUrl = generateUrl(ROUTES.MATERIALITY_TRACKER_MODULES, { initiativeId });
  const history = useHistory();
  const groupId = metricGroup._id;

  const handleClickTab = (viewMode: ViewMode) => {
    const url = generateUrl(ROUTES.MATERIALITY_TRACKER_MODULES, { initiativeId, groupId, viewMode });
    history.push(url);
  };

  const handleChangeMetricGroup = () => {
    // back to metric group dashboard
    history.push(generateUrl(ROUTES.MATERIALITY_TRACKER_MODULES, { initiativeId }));
  };

  const renderView = () => {
    switch (viewMode) {
      case ViewMode.Edit:
        return (
          <MetricGroupDetails
            metricGroup={metricGroup}
            fields={metricGroupFields}
            readOnly={false}
            isIconEditable={false}
            isPreferredAltCodesDisplayed={false}
            handleReload={handleReload}
            handleChangeMetricGroup={handleChangeMetricGroup}
            editUrl={generateUrl(ROUTES.MATERIALITY_TRACKER_MODULES, { initiativeId, groupId, viewMode })}
          />
        );
      case ViewMode.Configuration:
        return <MetricGroupConfiguration metricGroup={metricGroup} />;
      case ViewMode.AssignQuestions:
        return (
          <MetricGroupAssignment
            metricGroup={metricGroup}
            subGroup={subGroup}
            assignmentViewMode={assignmentViewMode}
            handleReload={handleReload}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className='ml-2'>
        <Breadcrumbs
          breadcrumbs={[]}
          rootUrl={rootUrl}
          rootLabel={
            <span>
              <i className='fal fa-caret-left' /> {`Custom ${QUESTION.SINGULAR} ${PACK.PLURAL}`}
            </span>
          }
        />
      </div>
      <h3 className='ml-3 my-3'>{metricGroup.groupName}</h3>
      <p className='mx-3'>
        Each custom {PACK.SINGULAR} for an assessment is organised into five sub-{PACK.PLURAL}, enabling organisations
        to concentrate on essential business elements. {QUESTION.CAPITALIZED_PLURAL} will be automatically added once
        the assessment is completed. You can easily modify the final {QUESTION.SINGULAR} list using the controls
        available on the configuration page.
      </p>
      <DashboardSection>
        <Menu
          className='mb-4'
          items={[
            {
              active: viewMode === ViewMode.Edit,
              onClick: () => handleClickTab(ViewMode.Edit),
              label: 'Details',
            },
            {
              active: viewMode === ViewMode.Configuration,
              onClick: () => handleClickTab(ViewMode.Configuration),
              label: 'Configuration',
            },
            {
              active: viewMode === ViewMode.AssignQuestions,
              onClick: () => handleClickTab(ViewMode.AssignQuestions),
              label: `Assign ${QUESTION.PLURAL}`,
            },
          ]}
        />
        <OutdatedMetricGroupAlert metricGroup={metricGroup} />
        {renderView()}
      </DashboardSection>
    </>
  );
};
